import React, { useMemo } from 'react';
import { DashboardAPIResponse } from 'src/pages/dashboard/Dashboard.interface';
import { buildStatusPieChartData } from 'src/components/dashboard/DashboardUtils';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SNOW_DATA_SERVICE_ENDPOINT } from 'src/config';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import PieChart from '@amzn/awsui-components-console/pie-chart';
import Button from '@amzn/awsui-components-console/button';
import EmptyState from 'src/components/commonComponents/EmptyState/EmptyState';

interface UserWorkflowMetricsChartProps {
	year: string;
	userId: string;
	workflowTypes: string[];
	month?: string;
	workflowNameMap?: Record<string, string>;
}

export default function UserWorkflowMetricsChart({ year, userId, workflowTypes, month, workflowNameMap }: UserWorkflowMetricsChartProps) {
	const queryParams = useMemo(() => (
		{ year, userId, workflowTypes: workflowTypes.join(','), ...(month && { month }) }
	), [year, userId, workflowTypes, month]);

	const { isLoading, data, error, fetchData } = useFetchForSDS<DashboardAPIResponse>({
		path: '/dashboard/workflows', method: 'GET', queryParams, url: SNOW_DATA_SERVICE_ENDPOINT,
	});

	const { total, pieData } = useMemo(() => buildStatusPieChartData(data?.items ?? []),[data],);

	if (isLoading) return <Box textAlign="center" padding={{ vertical: 'xxl' }}><Spinner size="large" /></Box>;
	if (error) return <Alert type="error" header="Error loading data" action={<Button onClick={() => fetchData()}>Retry</Button>}>{error}</Alert>;

	return (
		<PieChart
			data={pieData}
			detailPopoverContent={(segment, sum) => [
				{ key: 'Status', value: segment.title },
				{ key: 'Count', value: segment.value },
				{ key: 'Percentage', value: sum > 0 ? `${((segment.value / sum) * 100).toFixed(1)}%` : '0.0%' },
			]}
			detailPopoverSize="large"
			innerMetricDescription="total workflows"
			innerMetricValue={total.toString()}
			variant="donut" size="large" fitHeight hideFilter legendTitle="Workflow Status"
			ariaLabel="User workflow status distribution"
			empty={<EmptyState title="No workflow activity" description={`No activity for ${userId} in ${month ? `${year}-${month}` : year}.`} />}
		/>
	);
}
